import { ToastService } from './../../../../services/toast/toast.service';
import { ApiService } from 'src/app/services/api/api.service';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  FormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { SignupService } from 'src/app/services/signup/signup.service';
import fakerbr from 'faker-br';
import { MaskPipe } from 'ngx-mask';
import { AuthService } from 'src/app/services/auth/auth.service';

import Utils, { GetHelper, UtilsValidators } from 'src/app/helpers/utils';
import { StorageService } from 'src/app/services/storage/storage.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { async } from '@angular/core/testing';

@Component({
  selector: 'app-signup-unified-company-complementary-composition',
  templateUrl:
    './signup-unified-company-complementary-composition.component.html',
  styleUrls: [
    './signup-unified-company-complementary-composition.component.scss',
  ],
})
export class SignupUnifiedCompanyComplementaryCompositionComponent
  implements OnInit, OnDestroy
{
  @Input() userRole: string;

  showForm = true;

  shareholding: CompanyComplementaryCompositionValues[] = [];

  // Validador de nome completo (igual ao do signup-unified-personal)
  static fullNameValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const value: string = control.value.trim();

    // Divide o nome em palavras e remove strings vazias
    const words = value.split(/\s+/).filter((word) => word.length > 0);

    // Verifica se tem pelo menos 2 palavras
    if (words.length < 2) {
      return { minNames: true };
    }

    // Verifica se há abreviações (palavras com apenas uma letra ou palavras com ponto)
    for (const word of words) {
      // Verifica se é uma palavra com apenas uma letra (abreviação)
      if (word.length === 1) {
        return { abbreviation: true };
      }

      // Verifica se a palavra contém ponto (abreviação como "S.", "J.", etc.)
      if (word.includes('.')) {
        return { abbreviation: true };
      }
    }

    return null;
  }

  // Normaliza o nome completo (remove espaços múltiplos e espaços no início/fim)
  static normalizeFullName(value: string | null): string | null {
    if (!value) {
      return value;
    }
    return value.trim().replace(/\s+/g, ' ');
  }

  // Validador para limitar a 4 casas decimais
  static maxDecimalPlaces(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const value = String(control.value);
    const parts = value.split('.');

    if (parts.length > 1 && parts[1].length > 4) {
      return { maxDecimalPlaces: true };
    }

    return null;
  }

  form = this.formBuilder.group({
    document: [null, [Validators.required, UtilsValidators.cpfOrCnpj]],
    full_name: [null, [Validators.required]],
    percentage: [
      '',
      [
        Validators.required,
        Validators.max(100),
        Validators.min(0.01),
        SignupUnifiedCompanyComplementaryCompositionComponent.maxDecimalPlaces,
      ],
    ],
    title: ['', []],
    pep: [false, []],
    pep_relationship: [false, []],
    pep_relationship_document: [null, []],
    pep_relationship_name: [null, []],
    pep_bond: [null, []],
  });

  showWarning = false;

  addAnotherModal = false;
  removeModal = false;

  // Carbon Warning Modal properties
  showDocumentChangeModal = false;
  pendingPayload: any = null;
  pendingFinded: any = null;
  pendingValuesHelper: any = null;

  parentID: number | undefined = undefined;
  selectedID: number | undefined = undefined;

  partner: Partial<CompanyComplementaryCompositionValues>;

  loading = true;

  data: PartnerResponse[] = [];

  formattedData: PartnerResponse[] = [];

  sending = false;

  event: any;

  isSA = false;
  foundPerson = false;
  foundRelationPerson = false;

  isEditing = false;
  userDocument = '';
  invalidDocument = false;
  generalObj: any;
  removingData = false;
  /** Quando marcado, permite prosseguir mesmo com participação < 100% (restante pulverizado) */
  pulverizada = false;
  loadingPulverized = false;

  constructor(
    private formBuilder: FormBuilder,
    public signupService: SignupService,
    public maskPipe: MaskPipe,
    public authService: AuthService,
    public storageService: StorageService,
    private api: ApiService,
    private toast: ToastService,
    private route: ActivatedRoute
  ) {}

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  ngOnInit(): void {
    // Atualiza o step ao entrar na página
    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId) {
      this.signupService.updateUserStepCompany('register', 'composition', routeId);
    }

    this.getDataAndFillForm();
    this.getGeneralData();

    document.onkeyup = (event) => {
      if (event.key === 'Enter' && !this.getDisabled()) {
        if (this.showForm) {
          this.handleTriggerForm(0);
        }

        if (this.addAnotherModal) {
          this.handleTriggerForm(this.parentID, true);
        }
      }
    };

    this.form.controls.pep_relationship_document.valueChanges.subscribe(
      async (value) => {
        if (!value) {
          this.foundRelationPerson = false;
        }

        const document = Utils.onlyNumbers(value);

        if (document.length === 11 || document.length === 14) {
          const sameDocument =
            Utils.onlyNumbers(this.form.controls.document.value) === document;

          if (sameDocument) {
            const currentErrors =
              this.form.controls.pep_relationship_document.errors || {};
            this.form.controls.pep_relationship_document.setErrors({
              ...currentErrors,
              sameDocument: true,
            });
          } else {
            // Remove apenas erros customizados, preserva erros de validação
            const currentErrors =
              this.form.controls.pep_relationship_document.errors || {};
            const { sameDocument, ...validationErrors } = currentErrors;

            this.form.controls.pep_relationship_document.setErrors(
              Object.keys(validationErrors).length > 0 ? validationErrors : null
            );

            // Remove erro do campo document também
            const docCurrentErrors = this.form.controls.document.errors || {};
            const { sameDocument: docSameDoc, ...docValidationErrors } =
              docCurrentErrors;
            this.form.controls.document.setErrors(
              Object.keys(docValidationErrors).length > 0
                ? docValidationErrors
                : null
            );
          }

          const { name, foundPerson } = await this.api.getPersonName(value);

          this.foundRelationPerson = foundPerson;

          if (name) this.form.controls.pep_relationship_name.setValue(name);
        }
      }
    );

    this.form.controls.document.valueChanges.subscribe(async (value) => {
      if (!value) {
        this.foundPerson = false;
      }

      const document = Utils.onlyNumbers(value);

      const idx = this.data.findIndex((item) => item.id === this.parentID);

      let partnersArr = [];

      if (idx === -1) {
        partnersArr = this.data.filter((item) => !item.parent);
      } else {
        partnersArr = this.data.filter(
          (item) => item.parent === this.data[idx].id
        );
      }

      if (document.length === 14 || document.length === 11) {
        const sameDocument =
          Utils.onlyNumbers(
            this.form.controls.pep_relationship_document.value
          ) === document;

        if (sameDocument) {
          const currentErrors = this.form.controls.document.errors || {};
          this.form.controls.document.setErrors({
            ...currentErrors,
            sameDocument: true,
          });
          return;
        } else {
          // Remove apenas erros customizados, preserva erros de validação
          const currentErrors = this.form.controls.document.errors || {};
          const { sameDocument, alreadyAdded, isOwner, ...validationErrors } =
            currentErrors;

          this.form.controls.document.setErrors(
            Object.keys(validationErrors).length > 0 ? validationErrors : null
          );
          this.form.controls.pep_relationship_document.setErrors(null);
        }

        this.invalidDocument = false;

        const { name, foundPerson } = await this.api.getPersonName(value);

        this.foundPerson = foundPerson;
        if (name) this.form.controls.full_name.setValue(name);

        if (
          partnersArr.some(
            (item) => Utils.onlyNumbers(item.document) === document
          ) &&
          !this.isEditing
        ) {
          this.toast.show('error', 'Atenção', 'Documento já adicionado!');
          const currentErrors = this.form.controls.document.errors || {};
          this.form.controls.document.setErrors({
            ...currentErrors,
            alreadyAdded: true,
          });
        } else {
          // Remove apenas erros customizados, preserva erros de validação
          const currentErrors = this.form.controls.document.errors || {};
          const { sameDocument, alreadyAdded, isOwner, ...validationErrors } =
            currentErrors;

          this.form.controls.document.setErrors(
            Object.keys(validationErrors).length > 0 ? validationErrors : null
          );
        }
      }

      if (document.length === 14) {
        if (this.userDocument === document) {
          this.invalidDocument = true;
          const currentErrors = this.form.controls.document.errors || {};
          this.form.controls.document.setErrors({
            ...currentErrors,
            isOwner: true,
          });
        }
      }
    });

    this.form.controls.full_name.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (Utils.onlyLetters(value)) {
          this.form.controls.full_name.setErrors(null);
        } else {
          this.form.controls.full_name.setErrors({ onlyNumbers: true });
        }
      });

    this.event = this.signupService.sendComplementaryCompanyData.subscribe(
      async (value) => {
        if (value === 'complementaryComposition') {
          this.loading = true;
          try {
            this.signupService.setComplementaryCompanyStep(
              'complementaryEconomic'
            );
            this.signupService.changeCompanyStepEvent.emit('economic');
          } finally {
            // O loading será desativado pelo componente pai após a navegação
            setTimeout(() => {
              this.loading = false;
            }, 500);
          }
        }
      }
    );

    this.signupService.fillFormEvent.subscribe(() => {
      const pep_relationship = fakerbr.random.boolean();
      const isCompany = fakerbr.random.boolean();

      const document = isCompany
        ? this.maskPipe.transform(fakerbr.br.cnpj(), '00.000.000/0000-00')
        : this.maskPipe.transform(fakerbr.br.cpf(), '000.000.000-00');

      const full_name = isCompany
        ? fakerbr.company.companyName()
        : fakerbr.name.findName();

      this.form.patchValue({
        pep_relationship: pep_relationship,
        pep_relationship_document: pep_relationship
          ? this.maskPipe.transform(fakerbr.br.cpf(), '000.000.000-00')
          : null,
        pep_relationship_name: pep_relationship
          ? fakerbr.name.findName()
          : null,
        pep_bond: pep_relationship ? 'Sócio' : null,
        document: document,
        full_name: full_name,
        percentage: fakerbr.random.number({
          min: 0.01,
          max: 100,
        }),
        pep: fakerbr.random.boolean(),
      });
    });

    this.form.controls.pep_relationship.valueChanges.subscribe(() =>
      this.verifyPepRelationshipFields()
    );
  }

  async getGeneralData() {
    // Ativa loading apenas se getDataAndFillForm não estiver rodando
    const shouldShowLoading = !this.loading;
    if (shouldShowLoading) {
      this.loading = true;
    }
    try {
      const routeId = this.route.snapshot.paramMap.get('id');
      if (!routeId) {
        console.error('Register UUID não encontrado na rota');
        if (shouldShowLoading) {
          this.loading = false;
        }
        return;
      }

      const { data } = await this.api.get<ApiResponse<Company>>({
        route: 'api/registration/company/general/',
        params: { register_uuid: routeId },
        token: true,
      });

      // Se data é null, significa que ainda não tem dados cadastrados (normal)
      if (!data || data === null) {
        if (shouldShowLoading) {
          this.loading = false;
        }
        return;
      }

      this.userDocument = data.document.number;
    } catch (error) {
      console.log(error);
    } finally {
      // Desativa loading apenas se foi este método que o ativou
      if (shouldShowLoading) {
        this.loading = false;
      }
    }
  }

  verifyPepRelationshipFields() {
    if (this.form.controls.pep_relationship.value) {
      this.form.controls.pep_relationship_document.setValidators([
        Validators.required,
        UtilsValidators.cpfOrCnpj,
      ]);
      this.form.controls.pep_relationship_name.setValidators([
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(60),
        Validators.pattern(/^[a-zA-ZÀ-ÿ\s'-]+$/),
        SignupUnifiedCompanyComplementaryCompositionComponent.fullNameValidator,
      ]);
      this.form.controls.pep_bond.setValidators([Validators.required]);
    } else {
      this.form.controls.pep_relationship_document.setValidators([]);
      this.form.controls.pep_relationship_name.setValidators([]);
      this.form.controls.pep_bond.setValidators([]);
    }

    this.form.controls.pep_relationship_document.updateValueAndValidity();
    this.form.controls.pep_relationship_name.updateValueAndValidity();
    this.form.controls.pep_bond.updateValueAndValidity();
  }

  getShareholdersPercentage(item_id?: number) {
    let maxPercentage = 0;

    if (item_id !== undefined) {
      const partner = this.data.find((item) => item.id === item_id);
      if (partner) {
        maxPercentage = partner.shareholders.reduce((prev, curr) => {
          return curr.id !== this.selectedID
            ? Number(prev.toFixed(4)) + Number(curr.percentage.toFixed(4))
            : Number(prev.toFixed(4));
        }, 0);
      }
    } else {
      const rootObjects = this.data.filter((item) => item.parent === null);
      maxPercentage = rootObjects.reduce((prev, curr) => {
        return curr.id !== this.selectedID
          ? Number(prev.toFixed(4)) + Number(curr.percentage.toFixed(4))
          : Number(prev.toFixed(4));
      }, 0);
    }

    return Number(maxPercentage.toFixed(4));
  }

  getDisabled() {
    if (this.addAnotherModal) {
      return this.form.invalid;
    }

    if (this.showForm) {
      return this.form.invalid;
    }

    return false;
  }

  async handleTriggerForm(item_id?: number, toggleModal = false) {
    if (this.isEditing) {
      await this.sendData();
      return;
    }

    this.parentID = item_id != undefined ? item_id : 0;
    const maxPercentage = Number(
      (100 - Number(this.getShareholdersPercentage(item_id))).toPrecision(7)
    );

    this.form.controls.percentage.setValidators([
      Validators.required,
      Validators.max(maxPercentage),
      Validators.min(0.01),
      SignupUnifiedCompanyComplementaryCompositionComponent.maxDecimalPlaces,
    ]);

    this.form.controls.percentage.updateValueAndValidity();

    if (toggleModal) {
      if (this.addAnotherModal) {
        await this.sendData();
        this.addAnotherModal = false;
      } else {
        this.addAnotherModal = true;
        this.signupService.setCompanyFormStatus(
          'complementaryComposition',
          false
        );
      }
    } else {
      await this.sendData();
    }
  }

  formatPercentage(value: number) {
    const valueString = String(value);
    const isFloat = valueString.includes('.');

    if (isFloat) {
      const splitted = valueString.split('.');

      if (splitted[1].length === 1) {
        return `${valueString}0`;
      }
    }

    return valueString;
  }

  setFieldsByRole() {
    if (this.authService.user) {
      if (
        this.authService.user.active_register.register.role.slug ===
        `${this.userRole.toUpperCase()}-PJ-FI`
      ) {
        this.form.controls.document.setValidators([]);
        this.form.controls.percentage.setValidators([]);
      } else {
        this.form.controls.document.setValidators([Validators.required]);
        this.form.controls.percentage.setValidators([
          Validators.required,
          Validators.max(100),
          Validators.min(0.01),
          SignupUnifiedCompanyComplementaryCompositionComponent.maxDecimalPlaces,
        ]);
      }

      this.form.controls.document.updateValueAndValidity();
      this.form.controls.percentage.updateValueAndValidity();
    }
  }

  async handleCloseModal() {
    this.addAnotherModal = false;
    this.parentID = undefined;
    this.selectedID = undefined;
    this.isEditing = false;
    this.form.reset();

    this.updateFormStatus();
  }

  handleRemoveModal(item: PartnerResponse) {
    this.parentID = item.id;
    this.removeModal = true;
  }

  handleRemoveCancel() {
    this.removeModal = false;
    this.parentID = undefined;
    this.removingData = false;
  }

  getSlugLabel() {
    const label = `${this.userRole.toUpperCase()}-PJ-FI`;

    return label;
  }

  async removeData() {
    if (this.removingData) {
      return; // Previne múltiplas chamadas
    }

    const partner = this.data.find((item) => item.id === this.parentID);

    if (partner.shareholders.length > 0) {
      this.handleRemoveCancel();
      this.toast.show(
        'error',
        'Erro!',
        'Para remover um sócio, é necessário remover todos os seus dependentes.'
      );

      return;
    }

    this.removingData = true;

    try {
      const params = this.getRegisterParams();
      if (!params) {
        this.removingData = false;
        return;
      }

      await this.api.delete({
        route: `api/registration/company-shareholders/${this.parentID}/`,
        params: params,
        token: true,
      });

      const newData = this.data.filter((item) => item.id !== this.parentID);

      this.data = newData;

      this.getDataAndFillForm();

      this.removeModal = false;

      this.showForm = newData.length === 0;

      this.toast.show('info', 'Sucesso!', 'Sócio removido com sucesso!');
    } catch (error) {
      this.toast.show('error', 'Erro!', error.error.message);
    } finally {
      this.removingData = false;
    }
  }

  getCompanyName(): string {
    return this.generalObj?.data?.corporate_name || 'Grupo principal';
  }

  isCompositionComplete(): boolean {
    if (this.pulverizada && this.hasAtLeastOnePartner()) {
      return true;
    }
    const minimunValue = this.getMinimumPercentageValue();
    const totalPercentage = this.getShareholdersPercentage();
    return totalPercentage >= minimunValue || totalPercentage === 100;
  }

  hasAtLeastOnePartner(): boolean {
    return this.data && this.data.length >= 1;
  }

  onPulverizadaChange(): void {
    this.updateFormStatus();
    this.savePulverized();
  }

  private async savePulverized(): Promise<void> {
    const params = this.getRegisterParams();
    if (!params || !this.hasAtLeastOnePartner()) return;
    this.loadingPulverized = true;
    try {
      await this.api.patch<any>({
        route: 'api/registration/company-shareholders/',
        params,
        token: true,
        body: { pulverized: this.pulverizada },
      });
    } catch (err) {
      console.warn('Erro ao salvar pulverized:', err);
      this.toast.show('error', 'Erro', 'Não foi possível atualizar a pulverização.');
    } finally {
      this.loadingPulverized = false;
    }
  }

  private updateFormStatus(): void {
    this.signupService.setCompanyFormStatus(
      'complementaryComposition',
      this.isCompositionComplete()
    );
  }

  formatData() {
    const firstObject: PartnerResponse = {
      id: undefined,
      percentage: 0,
      isOpen: true,
      pep: false,
      full_name: this.getCompanyName(),
      document: '',
      parent: null,
      title: '',
      pep_bond: '',
      pep_relationship: false,
      pep_relationship_document: '',
      pep_relationship_name: '',
      shareholders: [],
      register: '',
      collapsed: false,
    };

    // Função recursiva para processar shareholders e evitar duplicação
    const processShareholders = (item: PartnerResponse): PartnerResponse => {
      const processedItem = {
        ...item,
        collapsed: false,
        isOpen: item.isOpen !== undefined ? item.isOpen : true,
        shareholders: item.shareholders ? item.shareholders.map(processShareholders) : [],
      };
      return processedItem;
    };

    // Processa apenas os itens raiz (parent === null)
    const rootItems = this.data
      .filter((item) => item.parent === null)
      .map(processShareholders);

    // Adiciona os itens raiz ao grupo principal
    firstObject.shareholders = rootItems;

    // Retorna apenas o grupo principal (que contém os itens raiz com seus shareholders aninhados)
    this.formattedData = [firstObject];
  }

  /**
   * Obtém o register_uuid da URL (obrigatório, sem fallback)
   */
  private getRegisterUuid(): string | null {
    return this.route.snapshot.paramMap.get('id');
  }

  /**
   * Obtém os params com register_uuid para as chamadas da API
   */
  private getRegisterParams(): any {
    const routeId = this.getRegisterUuid();

    if (!routeId) {
      this.toast.show('error', 'Erro', 'Register UUID não encontrado na rota');
      return null;
    }

    return { register_uuid: routeId };
  }

  async getDataAndFillForm(showLoading = true) {
    this.loading = showLoading;
    try {
      const params = this.getRegisterParams();
      if (!params) {
        this.loading = false;
        return;
      }

      const response = await this.api.get<any>({
        route: 'api/registration/company-shareholders/',
        params: params,
        token: true,
      });

      // pulverized vem na raiz da resposta, não dentro de data (array de sócios)
      this.data = response.data || [];
      this.pulverizada = response.pulverized ?? false;

      if (this.data.length > 0) {
        this.showForm = false;
      }

      const routeId = this.route.snapshot.paramMap.get('id');
      if (!routeId) {
        console.error('Register UUID não encontrado na rota');
        return;
      }

      const newGeneralObj = await this.api.get<ApiResponse<Company>>({
        route: 'api/registration/company/general/',
        params: { register_uuid: routeId },
        token: true,
      });

      // Se data é null, significa que ainda não tem dados cadastrados (normal)
      if (!newGeneralObj || !newGeneralObj.data || newGeneralObj.data === null) {
        this.loading = false;
        return;
      }

      this.generalObj = newGeneralObj;
      
      // Formatar dados após carregar os dados gerais para ter o nome da empresa
      this.formatData();

      if (!this.hasAtLeastOnePartner()) {
        this.pulverizada = false;
      }

      const minimunValue = this.getMinimumPercentageValue();

      this.isSA = minimunValue === 25;

      this.updateFormStatus();
    } catch (error) {
      console.error(error);
      if (error.status !== 404) {
        this.toast.show('error', 'Erro', error);
      }
    }
    this.loading = false;
  }

  getMinimumPercentageValue() {
    const frameworks = this.signupService.choices().frameworks;

    let minimunValue = 25;

    if (frameworks) {
      minimunValue =
        frameworks.find(
          (frameworks) =>
            frameworks.value === String(this.generalObj.data.framework)
        ).label === 'Sociedade Anônima (S/A)'
          ? 25
          : 100;
    }

    return minimunValue;
  }

  async sendData() {
    this.sending = true;
    try {
      const valuesHelper = new GetHelper(this.form.value);

      const finded = this.data.find((item) => item.id === this.selectedID);

      let payload: any = {
        percentage: valuesHelper.get('percentage'),
        pep: valuesHelper.get('pep') ?? false,

        full_name: valuesHelper.get('full_name'),
        document: valuesHelper.get('document'),

        title: valuesHelper.get('title'),
      };

      if (this.form.controls.pep_relationship.value) {
        payload = {
          ...payload,
          pep_bond: valuesHelper.get('pep_bond'),
          pep_relationship: valuesHelper.get('pep_relationship'),
          pep_relationship_document: valuesHelper.get(
            'pep_relationship_document'
          ),
          pep_relationship_name:
            SignupUnifiedCompanyComplementaryCompositionComponent.normalizeFullName(
              valuesHelper.get('pep_relationship_name')
            ),
        };
      }

      if (finded) {
        const findedDocument = Utils.onlyNumbers(finded.document);
        const document = Utils.onlyNumbers(valuesHelper.get('document'));

        const changedToPF = findedDocument.length > document.length;

        if (!changedToPF) {
          payload['shareholders'] = finded.shareholders ?? [];
        } else {
          // Armazenar dados para continuar depois da confirmação
          this.pendingPayload = payload;
          this.pendingFinded = finded;
          this.pendingValuesHelper = valuesHelper;
          this.showDocumentChangeModal = true;
          this.sending = false;
          return;
        }
      }

      await this.executeRequest(payload, finded, valuesHelper);
    } catch (error) {
      console.error(error);

      this.toast.show('error', 'Erro', error.error.message);
    }
    this.sending = false;
  }

  async executeRequest(payload: any, finded: any, valuesHelper: any) {
    if (this.parentID > 0) {
      payload['parent'] = this.parentID;
    }

    // pulverized na raiz do body (um valor por cadastro, não por sócio)
    payload['pulverized'] = this.pulverizada;

    const params = this.getRegisterParams();
    if (!params) {
      this.sending = false;
      return;
    }

    const res = this.isEditing
      ? await this.api.patch<ApiResponse<PartnerResponse>>({
          route: `api/registration/company-shareholders/${this.selectedID}/`,
          params: params,
          token: true,
          body: payload,
        })
      : await this.api.post<ApiResponse<PartnerResponse>>({
          route: 'api/registration/company-shareholders/',
          params: params,
          token: true,
          body: payload,
        });

    this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');

    this.form.reset();

    this.getDataAndFillForm(false);

    if (this.isEditing) {
      const findedDocument = Utils.onlyNumbers(finded.document);
      const document = Utils.onlyNumbers(valuesHelper.get('document'));

      this.handleCloseModal();
      this.showWarning = findedDocument.length === 14 && document.length === 14;
    }
  }

  handleEdit(item: PartnerResponse) {
    this.parentID = item.parent ?? undefined;
    this.selectedID = item.id;
    this.isEditing = true;

    this.form.patchValue({
      document: item.document,
      full_name: item.full_name,
      percentage: item.percentage,
      pep: item.pep,
      pep_relationship: item.pep_relationship,
      pep_relationship_document: item.pep_relationship_document,
      pep_relationship_name: item.pep_relationship_name,
      pep_bond: item.pep_bond,
    });

    const maxPercentage =
      100 -
      Number(this.getShareholdersPercentage(this.parentID).toPrecision(7));

    this.form.controls.percentage.setValidators([
      Validators.required,
      Validators.max(maxPercentage),
      Validators.min(0.01),
      SignupUnifiedCompanyComplementaryCompositionComponent.maxDecimalPlaces,
    ]);

    this.form.controls.percentage.updateValueAndValidity();

    this.addAnotherModal = true;
    this.signupService.setCompanyFormStatus('complementaryComposition', false);
  }

  showChildrens() {}

  getSpace(index: number) {
    const multiplyNumber = this.getMultiplier(index);

    return multiplyNumber * 32;
  }

  getMultiplier(index: number) {
    let multiplyNumber = this.formattedData[index].id ? 1 : 0;
    let haveParent = this.formattedData[index].parent !== null;
    let parentId = 0;
    let findedIndex = index;

    while (haveParent) {
      multiplyNumber++;
      parentId = this.formattedData[findedIndex].parent;

      if (parentId === null) {
        haveParent = false;
        break;
      }

      findedIndex = this.formattedData.findIndex(
        (item) => item.id === parentId
      );

      haveParent = this.formattedData[findedIndex].parent !== null;
    }

    return multiplyNumber;
  }

  handleKeyEnter(event: any) {
    event.preventDefault();
    if (event.keyCode === 13 && this.form.valid) {
      //   this.triggerForm();
    }
  }

  handleSetCollapsed(index: number, isOpen: boolean) {
    const item = this.formattedData[index];
    
    if (!isOpen) {
      // Expandir: apenas marca como aberto
      item.isOpen = true;
      item.collapsed = false;
      return;
    }

    // Colapsar: marca como fechado
    item.isOpen = false;
    item.collapsed = true;
    
    // Se tiver shareholders aninhados, também colapsa eles recursivamente
    if (item.shareholders && item.shareholders.length > 0) {
      item.shareholders.forEach((shareholder) => {
        if (shareholder.shareholders && shareholder.shareholders.length > 0) {
          shareholder.isOpen = false;
          shareholder.collapsed = true;
        }
      });
    }
  }

  isCNPJ(document: string): boolean {
    if (!document) return false;
    // CNPJ formatado tem 18 caracteres (XX.XXX.XXX/XXXX-XX) e contém "/"
    // CNPJ sem formatação tem 14 dígitos (sem "/" e sem "-")
    // CPF formatado tem 14 caracteres (XXX.XXX.XXX-XX) e contém "-" mas não "/"
    // CPF sem formatação tem 11 dígitos
    if (document.includes('/')) {
      return true; // CNPJ formatado sempre tem "/"
    }
    if (document.length === 18) {
      return true; // CNPJ formatado tem 18 caracteres
    }
    if (document.length === 14 && !document.includes('-')) {
      return true; // CNPJ sem formatação tem 14 dígitos sem "-"
    }
    return false; // CPF ou formato inválido
  }

  handleSetCollapsedShareholderRecursive(parentItem: PartnerResponse, shareholderIndex: number, isOpen: boolean) {
    const shareholder = parentItem.shareholders[shareholderIndex];
    
    if (!shareholder) {
      return;
    }
    
    // Inicializa propriedades se não existirem
    if (shareholder.isOpen === undefined) {
      shareholder.isOpen = true;
    }
    if (shareholder.collapsed === undefined) {
      shareholder.collapsed = false;
    }
    
    if (!isOpen) {
      // Expandir: apenas marca como aberto
      shareholder.isOpen = true;
      shareholder.collapsed = false;
      return;
    }

    // Colapsar: marca como fechado
    shareholder.isOpen = false;
    shareholder.collapsed = true;
    
    // Se tiver shareholders aninhados, também colapsa eles recursivamente
    if (shareholder.shareholders && shareholder.shareholders.length > 0) {
      shareholder.shareholders.forEach((subShareholder) => {
        if (subShareholder.shareholders && subShareholder.shareholders.length > 0) {
          subShareholder.isOpen = false;
          subShareholder.collapsed = true;
          // Recursão para colapsar todos os níveis
          this.collapseAllNested(subShareholder);
        }
      });
    }
  }

  private collapseAllNested(shareholder: PartnerResponse) {
    if (shareholder.shareholders && shareholder.shareholders.length > 0) {
      shareholder.shareholders.forEach((sub) => {
        sub.isOpen = false;
        sub.collapsed = true;
        this.collapseAllNested(sub);
      });
    }
  }

  handleSetCollapsedShareholder(parentItem: PartnerResponse, shareholderIndex: number, isOpen: boolean) {
    const shareholder = parentItem.shareholders[shareholderIndex];
    
    if (!shareholder) {
      return;
    }
    
    // Inicializa propriedades se não existirem
    if (shareholder.isOpen === undefined) {
      shareholder.isOpen = true;
    }
    if (shareholder.collapsed === undefined) {
      shareholder.collapsed = false;
    }
    
    if (!isOpen) {
      // Expandir: apenas marca como aberto
      shareholder.isOpen = true;
      shareholder.collapsed = false;
      return;
    }

    // Colapsar: marca como fechado
    shareholder.isOpen = false;
    shareholder.collapsed = true;
    
    // Se tiver shareholders aninhados, também colapsa eles recursivamente
    if (shareholder.shareholders && shareholder.shareholders.length > 0) {
      shareholder.shareholders.forEach((subShareholder) => {
        if (subShareholder.shareholders && subShareholder.shareholders.length > 0) {
          subShareholder.isOpen = false;
          subShareholder.collapsed = true;
        }
      });
    }
  }

  // Carbon Warning Modal methods
  closeDocumentChangeModal() {
    this.showDocumentChangeModal = false;
    // Limpar dados pendentes quando cancelar
    this.pendingPayload = null;
    this.pendingFinded = null;
    this.pendingValuesHelper = null;
  }

  async confirmDocumentChange() {
    this.showDocumentChangeModal = false;
    this.sending = true;

    try {
      // Continuar com os dados armazenados, mas sem os shareholders (que serão removidos)
      await this.executeRequest(
        this.pendingPayload,
        this.pendingFinded,
        this.pendingValuesHelper
      );
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', error.error.message);
    }

    // Limpar dados pendentes
    this.pendingPayload = null;
    this.pendingFinded = null;
    this.pendingValuesHelper = null;
    this.sending = false;
  }

  getDocumentChangeModalTitle() {
    return 'Atenção!';
  }

  getDocumentChangeModalMessage() {
    return 'Ao alterar o tipo de documento, todos os dependentes serão removidos. Deseja continuar?';
  }

  getDocumentChangeConfirmText() {
    return 'Sim';
  }

  getDocumentChangeCancelText() {
    return 'Não';
  }
}
